/**
 * Demo-only "Custom canned answers" card.
 *
 * Lets the demoer add / edit / remove buyer-specific Q&A pairs that
 * Ask Puntt will reply with verbatim when any of the user-defined
 * trigger phrases appears in the chat. Stored in
 * `Enterprise.demoConfig.scriptedResponses` and consulted BEFORE the
 * hardcoded `SCRIPTED_RESPONSES` table in
 * `services/christo/app/handlers/catalyst/demoAskPunttResponses.ts`,
 * so a demoer can shadow / override the canon by adding an entry with
 * overlapping trigger phrases.
 *
 * Reset Demo wipes the entire `demoConfig` subdoc — every entry added
 * here disappears, restoring just the seeded canned responses.
 *
 * Gated behind `isDemoEnterprise()` at the call site so the card never
 * renders on a real tenant.
 */
import { Pencil, Plus, Trash } from "@phosphor-icons/react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  IconButton,
  Separator,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  createDemoScriptedResponse,
  deleteDemoScriptedResponse,
  type DemoScriptedResponse,
  listDemoScriptedResponses,
  updateDemoScriptedResponse,
} from "../../../services/demo";
import { errorMessage } from "../../../utils/http";

const QUERY_KEY = ["demo", "scripted-responses"] as const;

export function DemoScriptedResponsesCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listDemoScriptedResponses,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const [addOpen, setAddOpen] = useState(false);

  const rows = data?.scriptedResponses ?? [];

  return (
    <Card data-testid="settings-card-demo-scripted-responses">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            Custom canned answers
          </Heading>
          <Text size="2" color="gray">
            Add your own Q&amp;A pairs. When someone types a chat message
            containing any of the trigger phrases (anywhere in the message, not
            case-sensitive), Ask Puntt replies with your answer instead of
            running a real AI search. Useful for pre-baking buyer-specific
            answers before a meeting. Reset Demo deletes everything you add
            here.
          </Text>
        </Flex>

        {isLoading ? (
          <Text size="2" color="gray">
            Loading…
          </Text>
        ) : error ? (
          <Text size="2" color="red">
            Couldn&apos;t load your canned answers. Try refreshing.
          </Text>
        ) : rows.length === 0 ? (
          <Text size="2" color="gray">
            No custom answers yet. Click &quot;Add a canned answer&quot; below
            to create one.
          </Text>
        ) : (
          <Flex direction="column" gap="2">
            {rows.map((row) => (
              <ScriptedResponseRow
                key={row.id}
                row={row}
                onChanged={invalidate}
              />
            ))}
          </Flex>
        )}

        <Separator size="4" />

        {addOpen ? (
          <ScriptedResponseEditor
            mode="create"
            onCancel={() => setAddOpen(false)}
            onSaved={() => {
              setAddOpen(false);
              invalidate();
            }}
          />
        ) : (
          <Flex>
            <Button
              data-testid="settings-demo-scripted-responses-add"
              variant="soft"
              onClick={() => setAddOpen(true)}
              aria-label="Add a canned answer"
            >
              <Plus size={14} />
              Add a canned answer
            </Button>
          </Flex>
        )}
      </Flex>
    </Card>
  );
}

function ScriptedResponseRow({
  row,
  onChanged,
}: {
  row: DemoScriptedResponse;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => deleteDemoScriptedResponse(row.id),
    onSuccess: () => {
      setErrorMsg(null);
      onChanged();
    },
    onError: async (err) => {
      setErrorMsg(await errorMessage(err));
    },
  });

  if (editing) {
    return (
      <ScriptedResponseEditor
        mode="edit"
        initial={row}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          onChanged();
        }}
      />
    );
  }

  return (
    <Box
      className="rounded-md border border-puntt-neutral-gray-4 p-3"
      data-testid={`scripted-response-${row.id}`}
    >
      <Flex direction="column" gap="2">
        <Flex justify="between" align="start" gap="3">
          <Flex direction="column" gap="1" style={{ flex: 1 }}>
            <Text size="1" color="gray">
              When the chat contains any of these phrases:
            </Text>
            <Flex gap="1" wrap="wrap">
              {row.triggerPhrases.map((phrase, i) => (
                <Badge
                  key={i}
                  color="amber"
                  variant="soft"
                  size="1"
                  data-testid={`scripted-response-${row.id}-trigger-${i}`}
                >
                  {phrase}
                </Badge>
              ))}
            </Flex>
          </Flex>
          <Flex gap="1">
            <IconButton
              variant="soft"
              size="1"
              onClick={() => setEditing(true)}
              aria-label="Edit this canned answer"
              data-testid={`scripted-response-${row.id}-edit`}
            >
              <Pencil size={14} />
            </IconButton>
            <IconButton
              variant="soft"
              color="red"
              size="1"
              onClick={() => {
                if (
                  window.confirm(
                    "Remove this canned answer? Ask Puntt will go back to its real AI search for messages that used to match.",
                  )
                ) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              aria-label="Remove this canned answer"
              data-testid={`scripted-response-${row.id}-delete`}
            >
              <Trash size={14} />
            </IconButton>
          </Flex>
        </Flex>
        <Text size="1" color="gray">
          Ask Puntt replies with:
        </Text>
        <Box className="rounded bg-puntt-neutral-gray-2 p-2">
          <Text
            size="2"
            data-testid={`scripted-response-${row.id}-response`}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {row.response}
          </Text>
        </Box>
        {errorMsg ? (
          <Text size="1" color="red">
            {errorMsg}
          </Text>
        ) : null}
      </Flex>
    </Box>
  );
}

function ScriptedResponseEditor({
  mode,
  initial,
  onCancel,
  onSaved,
}: {
  mode: "create" | "edit";
  initial?: DemoScriptedResponse;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [phrasesText, setPhrasesText] = useState(
    initial?.triggerPhrases?.join("\n") ?? "",
  );
  const [response, setResponse] = useState(initial?.response ?? "");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reseat the local draft when the editor is reused for a different
  // row (mode flip or initial changes). Belt-and-braces: in practice
  // the parent re-mounts the editor when toggling, so this rarely
  // fires.
  useEffect(() => {
    setPhrasesText(initial?.triggerPhrases?.join("\n") ?? "");
    setResponse(initial?.response ?? "");
  }, [initial]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const phrases = phrasesText
        .split(/\n+/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      if (phrases.length === 0) {
        return Promise.reject(
          new Error("Add at least one trigger phrase, one per line."),
        );
      }
      if (response.trim().length === 0) {
        return Promise.reject(
          new Error("Write an answer Ask Puntt should reply with."),
        );
      }
      if (mode === "create") {
        return createDemoScriptedResponse({
          triggerPhrases: phrases,
          response: response.trim(),
        });
      }
      if (!initial) {
        return Promise.reject(new Error("Missing entry to edit."));
      }
      return updateDemoScriptedResponse(initial.id, {
        triggerPhrases: phrases,
        response: response.trim(),
      });
    },
    onSuccess: () => {
      setErrorMsg(null);
      onSaved();
    },
    onError: async (err) => {
      setErrorMsg(await errorMessage(err));
    },
  });

  return (
    <Box
      className="rounded-md border border-puntt-accent-6 bg-puntt-accent-2 p-3"
      data-testid={`scripted-response-editor-${mode}`}
    >
      <Flex direction="column" gap="2">
        <Heading as="h3" size="2" weight="bold">
          {mode === "create" ? "Add a canned answer" : "Edit canned answer"}
        </Heading>

        <Flex direction="column" gap="1">
          <Text
            as="label"
            size="1"
            color="gray"
            htmlFor="scripted-trigger-input"
          >
            Trigger phrases (one per line). Ask Puntt fires the answer when ANY
            phrase appears in the chat message, case-insensitive.
          </Text>
          <TextArea
            id="scripted-trigger-input"
            value={phrasesText}
            onChange={(e) => setPhrasesText(e.target.value)}
            placeholder={
              "For example:\nwhat industries\nwho do you serve\ntell me about your customers"
            }
            rows={4}
            data-testid="scripted-response-editor-phrases"
            aria-label="Trigger phrases, one per line"
          />
        </Flex>

        <Flex direction="column" gap="1">
          <Text
            as="label"
            size="1"
            color="gray"
            htmlFor="scripted-response-input"
          >
            Ask Puntt&apos;s reply (Markdown supported)
          </Text>
          <TextArea
            id="scripted-response-input"
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            placeholder="Write the answer Ask Puntt should give. You can use **bold**, *italics*, and bullet lists."
            rows={6}
            data-testid="scripted-response-editor-response"
            aria-label="The answer Ask Puntt should reply with"
          />
        </Flex>

        <Flex gap="2" align="center" wrap="wrap">
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            data-testid="scripted-response-editor-save"
            aria-label={
              mode === "create"
                ? "Save new canned answer"
                : "Save changes to canned answer"
            }
          >
            {saveMutation.isPending
              ? "Saving…"
              : mode === "create"
                ? "Save"
                : "Save changes"}
          </Button>
          <Button
            variant="soft"
            color="gray"
            onClick={onCancel}
            disabled={saveMutation.isPending}
            data-testid="scripted-response-editor-cancel"
            aria-label="Cancel editing"
          >
            Cancel
          </Button>
          {errorMsg ? (
            <Text size="1" color="red">
              {errorMsg}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Box>
  );
}
